//third party imports
import { Platform } from '@angular/cdk/platform';
import { Injectable } from '@angular/core';
import {
  Event,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from '@angular/router';

//local imports
import { Messenger } from '@app/shared';
import { UserLoggedInMessage } from '@core/messages';
import { ApplicationConfiguration } from '@core/configuration';
import { securityLog } from '../helpers';

import { AuthRouteHelper } from '../helpers';

@Injectable({
    providedIn: 'root',
})
export class TenantUrlEnforcer {
    private initialized = false;
    private rewriteTargetUrl: string | null = null;
    private authCompleted = false;

    constructor(
        private platform: Platform,
        private router: Router,
        private messenger: Messenger,
        private applicationConfig: ApplicationConfiguration,
        private authRouteHelper: AuthRouteHelper,
    ) { }

    public initialize(): Promise<void> {
        if (this.initialized)
        {
            securityLog.warn(`TenantUrlEnforcer: Already initialized, skipping re-initialization`);
            return Promise.resolve();
        }

        if (!this.platform.isBrowser) {
            securityLog.warn(`TenantUrlEnforcer: Not running in a browser, skipping initialization`);
            return Promise.resolve();
        }

        securityLog.log(`TenantUrlEnforcer: Initializing`);
        this.initialized = true;

        this.messenger.subscribe(this, UserLoggedInMessage, () => {
            this.authCompleted = true;
            this.enforceTenantUrl(this.router.url);
        });

        this.router.events.subscribe((event: Event) => {
            if (event instanceof NavigationStart) {
                if (!this.authCompleted) {
                    return;
                }

                this.enforceTenantUrl(event.url);
                return;
            }

            if (
                event instanceof NavigationEnd ||
                event instanceof NavigationCancel ||
                event instanceof NavigationError
            ) {
                this.rewriteTargetUrl = null;
            }
        });
        
        return Promise.resolve();
    }

    private enforceTenantUrl(url: string): void {
        const tenant = this.applicationConfig.tenant?.trim();

        if (!tenant) {
            securityLog.warn(`TenantUrlEnforcer: No tenant available, skipping tenant URL enforcement`);
            return;
        }

        if (this.authRouteHelper.isTechnicalNonTenantPath(url)) {
            securityLog.log(`TenantUrlEnforcer: Technical non-tenant path, skipping enforcement for URL: ${url}`);
            return;
        }

        const canonicalUrl = this.authRouteHelper.ensureTenantInUrl(url, tenant);
        if (canonicalUrl === url || canonicalUrl === this.rewriteTargetUrl) {
            securityLog.log(`TenantUrlEnforcer: URL is already canonical or rewrite target, skipping enforcement for URL: ${url}`);
            return;
        }

        securityLog.log(`TenantUrlEnforcer: Redirecting from ${url} to ${canonicalUrl}`);

        // TODO: Add diagnostics for rewrite/skip decisions under a broader diagnostics task.
        this.rewriteTargetUrl = canonicalUrl;
        this.router.navigateByUrl(canonicalUrl, { replaceUrl: true }).catch((navigationError) => {
            securityLog.error(`TenantUrlEnforcer: failed to navigate to canonical URL ${canonicalUrl}`, navigationError);
            this.rewriteTargetUrl = null;
        });
    }
}